import { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface SubmenuItem {
  label: string;
  path: string;
}

interface MenuItem {
  label: string;
  path?: string;
  submenu?: SubmenuItem[];
}

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [mobileOpenSubmenu, setMobileOpenSubmenu] = useState<string | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [hasTopBarTimer, setHasTopBarTimer] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const checkTopBarTimer = () => {
      try {
        const stored = localStorage.getItem('ibx_contadores');
        if (!stored) {
          setHasTopBarTimer(false);
          return;
        }

        const contadores = JSON.parse(stored);
        const ativo = contadores.find(
          (c: any) => c.ativo && c.location === 'top-bar'
        );

        setHasTopBarTimer(!!ativo);
      } catch (error) {
        setHasTopBarTimer(false);
      }
    };

    checkTopBarTimer();
    const interval = setInterval(checkTopBarTimer, 2000);
    return () => clearInterval(interval);
  }, []);

  // Fechar menu mobile ao mudar de rota
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileOpenSubmenu(null);
  }, [location.pathname]);

  // Limpar timeout ao desmontar
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const menuItems: MenuItem[] = [
    { label: 'Home', path: '/' },
    { label: 'Quem Somos', path: '/quem-somos' },
    { 
      label: 'Seguros',
      submenu: [
        { label: 'Incêndio Empresarial', path: '/servicos/incendio-empresarial' },
        { label: 'Seguro Auto', path: '/servicos/automovel' },
        { label: 'Seguro de Vida', path: '/servicos/vida-empresarial' },
        { label: 'Gestão de Contratos', path: '/servicos/gestao-contratos' },
        { label: 'Consultoria Técnica', path: '/servicos/consultoria-tecnica' },
        { label: 'Reavaliação Financeira', path: '/servicos/reavaliacao-financeira-seguros' },
        { label: 'Suporte e Acompanhamento', path: '/servicos/suporte-acompanhamento' }
      ]
    },
    { 
      label: 'Estratégia',
      submenu: [
        { label: 'Como Funciona', path: '/como-funciona' },
        { label: 'Estratégia de Contratação', path: '/estrategia' },
        { label: 'Profissionalização', path: '/profissionalizacao-contratacao-seguros' }
      ]
    },
    { 
      label: 'Conteúdo',
      submenu: [
        { label: 'Blog', path: '/blog' },
        { label: 'Comunidade', path: '/comunidade' },
        { label: 'Taxa de Corretagem', path: '/taxa-corretagem' },
        { label: 'FAQ', path: '/faq' }
      ]
    },
    { 
      label: 'Resultados',
      submenu: [
        { label: 'Casos de Sucesso', path: '/casos-de-sucesso' },
        { label: 'Depoimentos', path: '/depoimentos' }
      ]
    },
    { label: 'Contato', path: '/contato' }
  ];

  const handleMouseEnter = (label: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenSubmenu(label);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setOpenSubmenu(null);
    }, 300);
  };

  const toggleMobileSubmenu = (label: string) => {
    setMobileOpenSubmenu(mobileOpenSubmenu === label ? null : label);
  };

  const topOffset = hasTopBarTimer ? 'top-[60px]' : 'top-0';

  return (
    <>
      <nav
        className={`fixed w-full z-40 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md py-1.5 sm:py-2' : 'bg-white shadow-sm py-2 sm:py-3'
        } ${topOffset}`}
        role="navigation"
        aria-label="Navegação principal"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3 cursor-pointer flex-shrink-0" aria-label="IBX Brasil - Página inicial">
              <div className={`bg-gradient-to-br from-petrol-500 to-petrol-700 rounded-lg flex items-center justify-center shadow-lg transition-all duration-300 ${
                isScrolled ? 'w-8 h-8 sm:w-9 sm:h-9' : 'w-9 h-9 sm:w-10 sm:h-10'
              }`}>
                <i className={`ri-shield-check-line text-white transition-all duration-300 ${
                  isScrolled ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'
                }`} aria-hidden="true"></i>
              </div>
              <div className="flex flex-col">
                <span className={`font-bold text-petrol-900 transition-all duration-300 ${
                  isScrolled ? 'text-sm sm:text-base' : 'text-base sm:text-lg'
                }`} style={{ fontFamily: "'Playfair Display', serif" }}>
                  IBX Brasil
                </span>
                <span className="text-xs text-petrol-600 hidden sm:block">
                  Gestão de Riscos
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-7">
              {menuItems.map((item) => (
                <div
                  key={item.label}
                  className="relative group"
                  onMouseEnter={() => item.submenu && handleMouseEnter(item.label)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.path ? (
                    <Link
                      to={item.path}
                      className="text-sm font-medium text-petrol-700 hover:text-action-orange-500 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      className="flex items-center gap-1.5 text-sm font-medium text-petrol-700 hover:text-action-orange-500 transition-colors whitespace-nowrap cursor-pointer"
                      aria-expanded={openSubmenu === item.label}
                      aria-haspopup="true"
                      aria-label={`Menu ${item.label}`}
                    >
                      {item.label}
                      <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                        openSubmenu === item.label ? 'rotate-180' : ''
                      }`} aria-hidden="true"></i>
                    </button>
                  )}

                  {/* Dropdown Menu - Corrigido */}
                  {item.submenu && openSubmenu === item.label && (
                    <div 
                      className="absolute left-1/2 -translate-x-1/2 pt-2 z-50"
                      style={{ top: '100%' }}
                      role="menu"
                      aria-label={`Submenu ${item.label}`}
                    >
                      <div className="bg-white rounded-xl shadow-2xl border border-gray-100 py-2 min-w-[240px] animate-fadeIn">
                        {item.submenu.map((subitem) => (
                          <Link
                            key={subitem.path}
                            to={subitem.path}
                            className="block px-5 py-3 text-sm font-medium text-petrol-700 hover:text-action-orange-500 hover:bg-petrol-50 transition-colors cursor-pointer"
                            role="menuitem"
                          >
                            {subitem.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <a
                href="https://wa.me/5577988080505?text=Ol%C3%A1%2C%20gostaria%20de%20falar%20com%20a%20IBX%20Brasil."
                target="_blank"
                rel="nofollow noreferrer"
                className={`inline-flex items-center gap-2 bg-action-orange-500 text-white rounded-lg font-semibold hover:bg-action-orange-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer ${
                  isScrolled ? 'px-4 py-2 text-sm' : 'px-5 py-2.5 text-sm'
                }`}
                aria-label="Falar no WhatsApp"
              >
                <i className="ri-whatsapp-line text-lg" aria-hidden="true"></i>
                Falar no WhatsApp
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg text-petrol-700 hover:bg-petrol-50 transition-colors cursor-pointer"
              aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl sm:text-2xl`} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" id="mobile-menu">
          <div 
            className="fixed inset-0 bg-petrol-950/95 backdrop-blur-sm" 
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          ></div>
          <nav 
            className={`fixed left-0 right-0 bg-white shadow-2xl rounded-b-2xl overflow-hidden max-h-[calc(100vh-5rem)] overflow-y-auto ${
              hasTopBarTimer ? 'top-[100px]' : 'top-[50px]'
            }`}
            role="navigation"
            aria-label="Menu mobile"
          >
            <div className="px-4 sm:px-6 py-6 space-y-1">
              {menuItems.map((item) => (
                <div key={item.label}>
                  {item.path ? (
                    <Link
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-base font-medium text-petrol-700 hover:text-action-orange-500 hover:bg-petrol-50 transition-colors py-3 px-4 rounded-lg cursor-pointer"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <>
                      <button
                        onClick={() => toggleMobileSubmenu(item.label)}
                        className="w-full flex items-center justify-between text-base font-medium text-petrol-700 hover:text-action-orange-500 hover:bg-petrol-50 transition-colors py-3 px-4 rounded-lg cursor-pointer"
                        aria-expanded={mobileOpenSubmenu === item.label}
                        aria-controls={`submenu-${item.label}`}
                        aria-label={`${item.label} - ${mobileOpenSubmenu === item.label ? 'Fechar' : 'Abrir'} submenu`}
                      >
                        {item.label}
                        <i className={`ri-arrow-${mobileOpenSubmenu === item.label ? 'up' : 'down'}-s-line text-lg transition-transform`} aria-hidden="true"></i>
                      </button>
                      {mobileOpenSubmenu === item.label && item.submenu && (
                        <div className="ml-4 mt-1 space-y-1" id={`submenu-${item.label}`} role="menu">
                          {item.submenu.map((subitem) => (
                            <Link
                              key={subitem.path}
                              to={subitem.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block text-sm font-medium text-petrol-600 hover:text-action-orange-500 hover:bg-petrol-50 transition-colors py-2.5 px-4 rounded-lg cursor-pointer"
                              role="menuitem"
                            >
                              {subitem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              <a
                href="https://wa.me/5577988080505?text=Ol%C3%A1%2C%20gostaria%20de%20falar%20com%20a%20IBX%20Brasil."
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center justify-center gap-2 bg-action-orange-500 text-white px-6 py-4 rounded-xl text-base font-semibold hover:bg-action-orange-600 transition-all shadow-lg mt-4 whitespace-nowrap cursor-pointer"
                aria-label="Falar no WhatsApp"
              >
                <i className="ri-whatsapp-line text-xl" aria-hidden="true"></i>
                Falar no WhatsApp
              </a>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}